import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import FormField from '../../components/FormField';
import Badge from '../../components/Badge';
import { useSchemas, useRecentIssuances, useMyDIDs } from '../../api/hooks';
import IssuerSignerPicker from '../../components/IssuerSignerPicker';
import { issueSignedCredential, type IssuerSigner } from '../../lib/credentialSigning';

export default function CredentialIssuance() {
  // Only the issuer's own schemas, and only ones still open for issuance (the
  // API refuses a deactivated schema). This used to list every issuer's.
  const { data: allMine, loading: schemasLoading } = useSchemas('mine');
  const schemas = (allMine ?? []).filter((s) => !s.deactivated);
  const { data: myDIDs } = useMyDIDs();
  const defaultIssuerDid = (myDIDs ?? []).find((d) => d.status === 'active')?.did ?? (myDIDs ?? [])[0]?.did;
  const { data: recentIssuances, loading: recentLoading } = useRecentIssuances(defaultIssuerDid);
  const { t } = useTranslation('issuer');

  const [selectedSchema, setSelectedSchema] = useState('');
  const [holderDid, setHolderDid] = useState('');
  const [expiration, setExpiration] = useState('');
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [showPreview, setShowPreview] = useState(false);
  const [issued, setIssued] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [issueError, setIssueError] = useState('');
  const [issuedId, setIssuedId] = useState('');
  const [signer, setSigner] = useState<IssuerSigner | null>(null);

  const schema = (schemas ?? []).find((s) => s.id === selectedSchema);
  // Issue as the DID that authored the chosen schema — an issuer with several
  // DIDs may have created it under any of them.
  const issuerDid = schema?.author ?? defaultIssuerDid;

  const handleSchemaChange = (schemaId: string) => {
    setSelectedSchema(schemaId);
    setFieldValues({});
    setIssued(false);
  };

  const credentialJson = {
    '@context': ['https://www.w3.org/2018/credentials/v1'],
    type: ['VerifiableCredential', schema?.name ?? ''],
    issuer: issuerDid || 'did:adi:issuer:...',
    issuanceDate: new Date().toISOString(),
    expirationDate: expiration || undefined,
    credentialSubject: { id: holderDid, ...fieldValues },
  };

  // Today in YYYY-MM-DD, for both the date picker's floor and the guard below.
  const today = new Date().toISOString().slice(0, 10);
  const expirationInPast = expiration !== '' && expiration < today;

  const handleIssue = async () => {
    setIssuing(true);
    setIssueError('');
    if (!issuerDid) {
      setIssueError('You need an active DID to issue credentials. Please create a DID first.');
      setIssuing(false);
      return;
    }
    // A credential that expires before it is issued is dead on arrival. The
    // date picker already refuses past dates, but a typed value can still get
    // through, so the check is repeated here.
    if (expirationInPast) {
      setIssueError('Expiration date cannot be in the past. Choose today or a later date.');
      setIssuing(false);
      return;
    }
    if (!signer) {
      setIssueError('Choose how to sign: connect the issuer wallet, or paste the issuer DID\'s key.');
      setIssuing(false);
      return;
    }
    try {
      const result = await issueSignedCredential({
        issuer: issuerDid,
        schemaId: selectedSchema,
        subjectDid: holderDid,
        type: ['VerifiableCredential', schema?.name ?? ''],
        expirationDate: expiration ? new Date(expiration).toISOString() : undefined,
        credentialSubject: Object.fromEntries(
          Object.entries(fieldValues).map(([k, v]) => {
            const fieldDef = schema?.fields.find((f) => f.name === k);
            if (fieldDef?.type === 'number') return [k, v === '' ? undefined : Number(v)];
            if (fieldDef?.type === 'boolean') return [k, v === 'true' || v === '1' || v === 'yes'];
            return [k, v];
          })
        ),
      }, signer, issuerDid);
      setIssuedId(result.id);
      setIssued(true);
    } catch (e) {
      setIssueError(e instanceof Error ? e.message : t('credentialIssuance.issuanceFailed'));
    } finally {
      setIssuing(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-text-main">{t('credentialIssuance.title')}</h1>
          <p className="mt-1 text-sm text-text-muted">{t('credentialIssuance.subtitle')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
            {issued ? (
              <div className="py-8 text-center">
                <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-emerald-50">
                  <span className="material-symbols-outlined text-emerald-500" style={{ fontSize: 32 }}>verified</span>
                </div>
                <h2 className="text-lg font-bold text-text-main">{t('credentialIssuance.successTitle')}</h2>
                <p className="mt-2 text-sm text-text-muted">{t('credentialIssuance.successSubtitle')}</p>
                <div className="mx-auto mt-4 max-w-sm rounded-lg border border-border-subtle bg-slate-50 p-4">
                  <div className="space-y-3 text-xs">
                    <div className="flex items-start justify-between gap-4">
                      <span className="shrink-0 text-text-faint">{t('credentialIssuance.credentialId')}</span>
                      <span className="break-all text-right font-mono font-bold text-primary">{issuedId}</span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="shrink-0 text-text-faint">{t('credentialIssuance.schema')}</span>
                      <span className="font-bold text-text-main">{schema?.name}</span>
                    </div>
                    <div className="flex items-start justify-between gap-4">
                      <span className="shrink-0 text-text-faint">{t('credentialIssuance.holder')}</span>
                      <span className="break-all text-right font-mono font-bold text-text-main">{holderDid}</span>
                    </div>
                  </div>
                </div>
                <button className="btn-primary mt-6" onClick={() => { setIssued(false); setFieldValues({}); setHolderDid(''); setExpiration(''); setSelectedSchema(''); }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 18 }}>add_circle</span>
                  {t('credentialIssuance.issueAnother')}
                </button>
              </div>
            ) : schemasLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : (
              <div className="space-y-5">
                <h2 className="text-sm font-extrabold uppercase tracking-widest text-text-main">{t('credentialIssuance.details')}</h2>

                <FormField
                  as="select"
                  label={t('credentialIssuance.schemaLabel')}
                  value={selectedSchema}
                  onChange={(e) => handleSchemaChange((e.target as HTMLSelectElement).value)}
                >
                  <option value="">{t('credentialIssuance.schemaPlaceholder')}</option>
                  {(schemas ?? []).map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </FormField>

                <FormField
                  label={t('credentialIssuance.holderLabel')}
                  placeholder={t('credentialIssuance.holderPlaceholder')}
                  value={holderDid}
                  onChange={(e) => setHolderDid((e.target as HTMLInputElement).value)}
                />

                <FormField
                  label={t('credentialIssuance.expirationLabel')}
                  type="date"
                  value={expiration}
                  min={today}
                  onChange={(e) => setExpiration((e.target as HTMLInputElement).value)}
                  hint={t('credentialIssuance.expirationHint')}
                  error={expirationInPast ? 'Expiration date cannot be in the past.' : undefined}
                />

                {schema && (
                  <div className="border-t border-border-subtle pt-5">
                    <h3 className="mb-3 text-xs font-bold text-text-muted">{t('credentialIssuance.subjectLabel')}</h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      {schema.fields.map((field) => (
                        <FormField
                          key={field.name}
                          label={`${field.name}${field.required ? ' *' : ''}`}
                          type={field.type === 'date' ? 'date' : field.type === 'number' ? 'number' : 'text'}
                          placeholder={t('credentialIssuance.fieldPlaceholder', { field: field.name })}
                          value={fieldValues[field.name] || ''}
                          onChange={(e) => setFieldValues({ ...fieldValues, [field.name]: (e.target as HTMLInputElement).value })}
                        />
                      ))}
                    </div>
                  </div>
                )}

                <IssuerSignerPicker did={issuerDid} onChange={setSigner} />

                {issueError && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{issueError}</div>
                )}

                <div className="flex gap-3 border-t border-border-subtle pt-5">
                  <button className="btn-secondary" onClick={() => setShowPreview(!showPreview)}>
                    <span className="material-symbols-outlined" style={{ fontSize: 18 }}>code</span>
                    {showPreview ? t('credentialIssuance.previewHide') : t('credentialIssuance.previewShow')}
                  </button>
                  <button className="btn-primary ml-auto" onClick={handleIssue} disabled={!selectedSchema || !holderDid || !signer || issuing}>
                    <span className="material-symbols-outlined" style={{ fontSize: 18 }}>verified</span>
                    {issuing ? t('credentialIssuance.issuing') : t('credentialIssuance.issue')}
                  </button>
                </div>

                {showPreview && (
                  <pre className="overflow-x-auto rounded-lg border border-border-subtle bg-slate-50 p-4 font-mono text-xs text-text-main">
                    {JSON.stringify(credentialJson, null, 2)}
                  </pre>
                )}
              </div>
            )}
          </div>
        </div>

        <div>
          <div className="mb-4 flex items-center gap-2">
            <span className="material-symbols-outlined text-text-muted" style={{ fontSize: 18 }}>history</span>
            <h2 className="text-sm font-extrabold uppercase tracking-widest text-text-main">{t('credentialIssuance.recent')}</h2>
          </div>
          {recentLoading ? (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="space-y-3">
              {(recentIssuances ?? []).map((vc) => (
                <div key={vc.id} className="rounded-xl border border-border-subtle bg-white p-4 transition-shadow hover:shadow-md">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs font-bold text-primary">{vc.id}</span>
                    <Badge variant={vc.status === 'active' ? 'success' : 'error'}>{vc.status}</Badge>
                  </div>
                  <p className="mt-2 text-xs font-bold text-text-main">{vc.schema}</p>
                  <p className="mt-1 truncate font-mono text-[10px] text-text-faint">{vc.holder}</p>
                  <p className="mt-1 text-[10px] text-text-faint">{vc.issuedAt}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
